import type { CohortBlock } from '../lib/types';
import { clamp } from '../lib/format';
import { InfoPopover } from './InfoPopover';
import styles from './CohortPercentilePanel.module.css';

interface Props {
  cohort: CohortBlock;
  className?: string;
}

export function CohortPercentilePanel({ cohort, className }: Props) {
  return (
    <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <header className={styles.header}>
        <div>
          <div className={styles.eyebrowRow}>
            <span className={styles.eyebrow}>Peer Set</span>
            <InfoPopover
              label="What is the peer set comparison?"
              content={
                <span>
                  Each dimension is compared against the same dimension in {cohort.peerCount} similar documents
                  ({cohort.peerSet}, {cohort.windowLabel}). The bar shows the cohort's P10–P90 range, the tick is
                  the cohort mean, and the navy marker is where this document falls. <strong>P{cohort.overallPercentile}</strong>{' '}
                  means this document scored higher than {cohort.overallPercentile}% of the peer set on the verdict score overall.
                </span>
              }
            />
          </div>
          <h3 className={styles.peer}>{cohort.peerSet}</h3>
          <p className={styles.meta}>
            {cohort.peerCount} documents · {cohort.windowLabel}
          </p>
        </div>
        <div className={styles.overall}>
          <span className={styles.overallLabel}>Overall</span>
          <span className={[styles.overallValue, 'mono'].join(' ')}>
            P{cohort.overallPercentile}
          </span>
          <span className={styles.overallNote}>
            {cohort.overallPercentile >= 75 ? 'Above peer set' : cohort.overallPercentile <= 25 ? 'Below peer set' : 'Mid peer set'}
          </span>
        </div>
      </header>

      <ul className={styles.statList}>
        {cohort.stats.map((s) => {
          const valuePos = clamp(s.value, 0, 1) * 100;
          const meanPos = clamp(s.cohortMean, 0, 1) * 100;
          const p10Pos = clamp(s.cohortP10, 0, 1) * 100;
          const p90Pos = clamp(s.cohortP90, 0, 1) * 100;
          return (
            <li key={s.label} className={styles.statRow}>
              <span className={styles.statLabel}>{s.label}</span>
              <div className={styles.bandWrap}>
                <div className={styles.bandTrack}>
                  <div className={styles.bandFill} style={{ left: `${p10Pos}%`, width: `${p90Pos - p10Pos}%` }} />
                  <div className={styles.meanMark} style={{ left: `${meanPos}%` }} aria-label="cohort mean" />
                  <div className={styles.valueMark} style={{ left: `${valuePos}%` }} aria-label="this document" />
                </div>
                <span className={[styles.percentile, 'mono'].join(' ')}>P{s.percentile}</span>
              </div>
            </li>
          );
        })}
      </ul>
      <p className={styles.legend}>
        <span className={styles.legendBand} /> P10–P90 cohort band ·
        <span className={styles.legendMean} /> mean ·
        <span className={styles.legendValue} /> this document
      </p>
    </div>
  );
}
